// src/components/TrendingTicker.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaPause, FaPlay, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./auth/AuthModal";
import { getTokenPrice } from "../services/api";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");

// Tailwind colors per chain badge
const CHAIN_COLORS = {
  eth: "bg-blue-600",
  bsc: "bg-yellow-500 text-black",
  solana: "bg-green-500",
  polygon: "bg-purple-500",
  arbitrum: "bg-cyan-500",
  base: "bg-blue-400",
  avalanche: "bg-red-500",
  optimism: "bg-pink-500",
  fantom: "bg-indigo-500",
};

function chainKey(x) {
  const c = String(x || "").toLowerCase();
  if (c === "ethereum") return "eth";
  return c;
}

// 👉 map ticker chain key to your TokenPage route segment
function chainKeyToPathSegment(ck) {
  const c = String(ck || "").toLowerCase();
  if (c === "eth") return "ethereum";
  // others already match your routes
  // (bsc, polygon, arbitrum, base, avalanche, optimism, solana, fantom, etc.)
  return c;
}

function formatUsd(n) {
  const v = Number(n || 0);
  if (!isFinite(v)) return "$-";
  if (v === 0) return "$0.00";
  if (v >= 1000) return `$${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const digits = v < 0.01 ? 8 : v < 1 ? 6 : 4;
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: digits })}`;
}

/**
 * Props:
 * - mode: "trending" | "memes" (default "trending")
 * - endpoint?: override API URL
 * - refreshMs?: number (default 60000)
 * - className?: extra classes on wrapper
 */
const TrendingTickerMajor = ({ mode = "trending", endpoint, refreshMs = 60000, className = "" }) => {
  const [raw, setRaw] = useState([]);
  const [paused, setPaused] = useState(false);

  // 🔐 Auth (same as MainLayout)
  const { user, logout } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const navigate = useNavigate();
  const userDisplay = user?.displayName || user?.username || user?.email || "";
  const avatarLetter = (userDisplay || user?.email || "?").toString().trim().charAt(0).toUpperCase();

  const apiUrl =
    endpoint ||
    (mode === "memes" ? `${API_BASE}/api/memes/trending` : `${API_BASE}/api/trending`);

  useEffect(() => {
    let stop = false;
    async function run() {
      try {
        const r = await fetch(apiUrl, { cache: "no-store" });
        const j = await r.json();
        const items = Array.isArray(j) ? j : (j?.items || j?.mints || j?.data || j || []);
        if (!stop) setRaw(items);
      } catch (e) {
        console.error("Failed to fetch trending tokens:", e);
      }
    }
    run();
    const t = refreshMs ? setInterval(run, refreshMs) : null;
    return () => {
      stop = true;
      if (t) clearInterval(t);
    };
  }, [apiUrl, refreshMs]);

  const [rows, setRows] = useState([]);
  useEffect(() => {
    let stop = false;
    async function enrich() {
      const src = Array.isArray(raw) ? raw : [];
      const out = new Array(src.length);
      let i = 0;
      const concurrency = 6;
      const pickAddr = (t) =>
        t?.address ||
        t?.tokenAddress ||
        t?.token_address ||
        t?.contract_address ||
        t?.mint ||
        "";
      const pickChain = (t) => t?.chainKey || chainKey(t?.chain || "eth");
      async function worker() {
        while (i < src.length) {
          const idx = i++;
          const t = src[idx];
          const addr = String(pickAddr(t) || "").toLowerCase();
          const ch = String(pickChain(t) || "").toLowerCase();
          let price = null;
          let chg = null;
          try {
            if (addr && ch) {
              const p = await getTokenPrice(ch, addr);
              const pv =
                p?.usdPrice ?? p?.priceUsd ?? p?.price_usd ?? p?.price ?? null;
              price = isFinite(Number(pv)) ? Number(pv) : null;
              const cv =
                p?.usdPrice24hrPercentChange ??
                p?.price_change_24h ??
                p?.change24h ??
                null;
              chg = isFinite(Number(cv)) ? Number(cv) : null;
            }
          } catch {}
          out[idx] = {
            ...t,
            priceUsd: price ?? t?.priceUsd ?? t?.usdPrice ?? 0,
            usdPrice24hrPercentChange:
              chg ?? t?.usdPrice24hrPercentChange ?? t?.priceChange24h ?? 0,
          };
        }
      }
      await Promise.all(Array.from({ length: concurrency }, worker));
      if (!stop) setRows(out);
    }
    enrich();
    return () => {
      stop = true;
    };
  }, [raw]);

  // rank per chain so badges are consistent
  const ranked = useMemo(() => {
    const cnt = {};
    return (rows || []).map((t) => {
      const ck = chainKey(t.chain || t.chainKey || "eth");
      cnt[ck] = (cnt[ck] || 0) + 1;
      return { ...t, chainKey: ck, rank: cnt[ck] };
    });
  }, [rows]);

  // duplicate for seamless marquee
  const marqueeItems = useMemo(() => [...ranked, ...ranked], [ranked]);

  return (
    <div className={`sticky top-0 lg:top-0 bg-black text-white border-b border-white/10 overflow-hidden z-[9] ${className}`}>
      {/* subtle background glow */}
      <div className="pointer-events-none absolute inset-0 bg-black/" />

      {/* three zones: left label, center ticker, right auth */}
      <div className="grid grid-cols-1 sm:grid-cols-[auto,1fr,auto] items-center gap-3 h-12 sm:h-14 pl-2 lg:pl-20 pr-2">
        {/* label & pause (hidden on mobile for cleaner UI) */}
        <div className="hidden sm:flex items-center gap-2 pr-2 border-r border-white/10">
          <div className="text-[10px] tracking-wider uppercase text-white/60">
            {mode === "memes" ? "Trending Memes" : "HOT"}
          </div>
          <button
            onClick={() => setPaused((p) => !p)}
            className="px-1.5 py-0.5 text-[11px] rounded text-white/70 hover:text-white bg-white/5 hover:bg-white/10"
            aria-label={paused ? "Play ticker" : "Pause ticker"}
            title={paused ? "Play" : "Pause"}
          >
            {paused ? <FaPlay /> : <FaPause />}
          </button>
        </div>

        {/* ticker track (center) */}
        <div className="relative overflow-hidden">
          {/* compact pause control for mobile */}
          <button
            onClick={() => setPaused((p) => !p)}
            className="sm:hidden absolute left-2 top-1.5 px-1.5 py-0.5 text-[11px] rounded text-white/70 hover:text-white bg-white/5 hover:bg-white/10 z-10"
            aria-label={paused ? "Play ticker" : "Pause ticker"}
            title={paused ? "Play" : "Pause"}
          >
            {paused ? <FaPlay /> : <FaPause />}
          </button>
          <div
            className={`flex gap-6 items-center whitespace-nowrap will-change-transform animate-marquee-slow ${
              paused ? "paused" : ""
            }`}
          >
            {marqueeItems.map((token, i) => {
              const pct = Number(
                token.usdPrice24hrPercentChange ?? token.priceChange24h ?? token.change24h ?? 0
              );
              const up = pct >= 0;
              const ck = token.chainKey || chainKey(token.chain || "eth");
              const badgeColor = CHAIN_COLORS[ck] || "bg-gray-600";
              const logo = token.imageUrl || token.logo || token.icon;
              const sym = token.symbol || token.baseToken?.symbol || token.ticker || "TKN";
              const price = token.usdPrice ?? token.priceUsd ?? token.price_usd ?? 0;
              const address = String(
                token.address || token.token_address || token.contract_address || token.mint || ""
              );

              // 👉 Link to Token Page instead of /explorer/{address}
              const pathChain = chainKeyToPathSegment(ck);
              const toUrl = address ? `/${pathChain}/${address}` : "#";

              const Pill = ({ children }) =>
                address ? (
                  <Link to={toUrl} className="no-underline" title={`Open ${sym} on ${pathChain.toUpperCase()}`}>
                    {children}
                  </Link>
                ) : (
                  <span>{children}</span>
                );

              return (
                <div key={`${sym}-${ck}-${i}`} className="min-w-max">
                  <Pill>
                    <div className="group relative inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold ${badgeColor}`}
                        title={ck.toUpperCase()}
                      >
                        {ck.toUpperCase()}
                      </span>
                      {logo ? (
                        <img src={logo} alt={sym} className="w-3.5 h-3.5 rounded-full" />
                      ) : (
                        <span className="inline-block w-3.5 h-3.5 rounded-full bg-white/15" />
                      )}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold">{sym}</span>
                        <span className="text-[11px] text-white/70">{formatUsd(price)}</span>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] ${
                          up ? "text-emerald-400" : "text-rose-400"
                        }`}
                        title={`${pct.toFixed(2)}% (24h)`}
                      >
                        {up ? <FaArrowUp /> : <FaArrowDown />}
                        {isFinite(pct) ? pct.toFixed(2) : "0.00"}%
                      </span>
                      <span className="absolute inset-0 transition-opacity rounded-full opacity-0 pointer-events-none -z-10 group-hover:opacity-100 blur-lg bg-gradient-to-r from-indigo-600/15 via-purple-600/15 to-pink-600/15" />
                    </div>
                  </Pill>
                </div>
              );
            })}
          </div>
        </div>

        {/* auth controls (right) — same pattern as MainLayout */}
        <div className="hidden lg:flex items-center justify-end w-full">
          {user ? (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/profile")}
                className="flex items-center gap-3 group"
                title="Open Profile"
              >
                <div className="flex items-center justify-center w-8 h-8 font-bold text-white bg-gray-700 rounded-full group-hover:ring-2 group-hover:ring-white/20">
                  {avatarLetter}
                </div>
                <span className="hidden text-sm text-white/80 group-hover:underline sm:inline">
                  {userDisplay}
                </span>
              </button>

              <button onClick={logout} className="text-xs text-red-400 underline">
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAuth(true)}
              className="px-4 py-1.5 text-sm text-white btn rounded"
              title="Login or create an account"
            >
              🔐 Login / Register
            </button>
          )}
        </div>
      </div>

      {/* Local auth modal (shares the same component as MainLayout) */}
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}

      <style>{`
        .animate-marquee-slow {
          display: inline-flex;
          animation: marquee 1000s linear infinite;
        }
        .paused { animation-play-state: paused; }
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

export default TrendingTickerMajor;
